import React, { forwardRef, useState } from 'react';
import { css } from '../../customEmotion';
import { makeStyles } from '@material-ui/core/styles';

import NestedAccordionSelectGroup, {
  OptionData,
} from './components/NestedAccordionSelectGroup';
import GreyBlock from './components/GreyBlock';
import Placeholder from './components/Placeholder';
import Select, { SelectProps } from '../Select';
import Typography from '../Typography';
import { useMUITheme } from '../styles/theme';

const useSelectStyle = makeStyles(theme => ({
  select: {
    padding: '5px 8px',
    display: 'inline-flex',
    backgroundColor: theme.palette.common.white,
    height: '30px',
  },
}));

interface NestedAccordionSelectProps extends SelectProps {
  options: OptionData[];
  value: string[];
  handleChangeValue: (value: string | string[]) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  maxHeight?: number;
  searchable?: boolean;
  loading?: boolean;
  error?: boolean;
  errorText?: string;
}

const NestedAccordionSelect = forwardRef<
  HTMLDivElement,
  NestedAccordionSelectProps
>(
  (
    {
      value,
      handleChangeValue,
      options,
      variant = 'outlined',
      placeholder = '',
      hint = '',
      disabled = false,
      maxHeight = 300,
      searchable = false,
      loading = false,
      error = false,
      errorText = '',
      ...rest
    },
    ref
  ) => {
    const selectStyle = useSelectStyle();

    const theme = useMUITheme();
    const [searchText, setSearchText] = useState('');

    const getRenderTexts = React.useCallback(
      (values: string[]) =>
        values
          .filter(v => {
            // 過濾掉含有非數字的值：頁面、題組、選項組
            const splitValue = v.split('-');
            return !splitValue.some(sv => isNaN(Number(sv)));
          })
          .map(value => {
            const findOptionText = (
              options: OptionData[],
              targetValue: string
          ): string => {
            for (const option of options) {
              if (option.value === targetValue) {
                return option.text;
              }
              if (option.children) {
                const childText = findOptionText(option.children, targetValue);
                if (childText) {
                  return childText;
                }
              }
            }
            return '';
          };
          return findOptionText(options, value);
        }),
      [options]
    );

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchText) return options;

      const filterOptions = (items: OptionData[]): OptionData[] => {
        return items
          .map(item => {
            const matches = item.text
              .toLowerCase()
              .includes(searchText.toLowerCase());
            const children = item.children && filterOptions(item.children);
            const hasMatchingChildren = children && children.length > 0;

            if (matches || hasMatchingChildren) {
              return {
                value: item.value,
                text: item.text,
                children: children,
              };
            }
            return null;
          })
          .filter((item): item is NonNullable<typeof item> => item !== null);
      };

      return filterOptions(options);
    }, [options, searchable, searchText]);

    const onChange = (clickValue: string | string[]) => {
      if (Array.isArray(clickValue)) {
        handleChangeValue(clickValue);
      } else {
        const isSelected = value.includes(clickValue);
        if (isSelected) {
          handleChangeValue(value.filter(v => v !== clickValue));
        } else {
          handleChangeValue(value.concat(clickValue));
        }
      }
    };

    return (
      <Select
        {...rest}
        ref={ref}
        value={''}
        variant={variant}
        displayEmpty
        fullWidth
        classes={selectStyle}
        disabled={disabled || loading}
        error={error}
        renderValue={
          value.length === 0
            ? () => <Placeholder text={placeholder} disabled={disabled} />
            : () => (
                <div
                  className={css`
                    display: flex;
                    overflow: hidden;
                    white-space: nowrap;
                  `}
                >
                  {getRenderTexts(value).map((text, index) => (
                    <GreyBlock key={`${text}-${index}`} text={text} />
                  ))}
                </div>
              )
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight,
            },
          },
        }}
      >
        {hint && (
          <Typography
            variant="body2"
            className={css`
              color: ${theme.palette.grey['500']};
              margin-bottom: 4px;
            `}
          >
            {hint}
          </Typography>
        )}
        {error && errorText && (
          <Typography
            variant="body2"
            className={css`
              color: ${theme.palette.error.main};
              margin-bottom: 4px;
            `}
          >
            {errorText}
          </Typography>
        )}
        {searchable && (
          <div
            className={css`
              padding: 8px;
              border-bottom: 1px solid ${theme.palette.divider};
            `}
          >
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="搜尋..."
              className={css`
                width: 100%;
                padding: 8px;
                border: 1px solid ${theme.palette.grey[300]};
                border-radius: 4px;
                outline: none;
                &:focus {
                  border-color: ${theme.palette.primary.main};
                }
              `}
            />
          </div>
        )}
        {loading ? (
          <div
            className={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 16px;
            `}
          >
            <Typography variant="body2" color="textSecondary">
              載入中...
            </Typography>
          </div>
        ) : (
          filteredOptions.map((option, optionIndex) => (
            <NestedAccordionSelectGroup
              key={`${option.value}-${optionIndex}`}
              option={option}
              handleChangeValue={onChange}
              selectedValue={value}
            />
          ))
        )}
      </Select>
    );
  }
);

NestedAccordionSelect.displayName = 'NestedAccordionSelect';

export default NestedAccordionSelect;
